import {
  Clear as ClearIcon,
  ContentCopy as CopyIcon,
  ContentPaste as PasteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  Chip,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Select,
  MenuItem,
  Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as dates from "common/dates";
import SubmissionSelector from "components/admin/SubmissionSelector";
import Button from "components/common/Button";
import TextField from "components/common/TextField";
import { useSnackbar } from "notistack";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import {
  useUpdateUserWhitelistListMutation,
  useUpdateUserWhitelistMutation,
} from "services/adminService";
import { useListSubmissionsQuery } from "services/submissionsService";

/**
 * WhitelistManagement - Buttons for managing the whitelist
 */
const WhitelistManagement = ({
  hasUnsavedChanges,
  submissionIds,
  onClear,
  onReload,
  onAddIds,
}) => {
  const [selectPasteTarget, setSelectPasteTarget] = useState(false);
  const pasteInputRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  // Detect macOS for keyboard shortcut display
  const isMac = useMemo(() => {
    return navigator.userAgent.toLowerCase().includes("mac");
  }, []);

  // Focus paste input when it becomes visible
  useEffect(() => {
    if (selectPasteTarget && pasteInputRef.current) {
      pasteInputRef.current.focus();
    }
  }, [selectPasteTarget]);

  const reloadTooltip = hasUnsavedChanges
    ? "Reload and discard unsaved changes"
    : "No unsaved changes";

  const handleCopy = async () => {
    if (submissionIds.length === 0) {
      enqueueSnackbar("No submission IDs to copy", { variant: "warning" });
      return;
    }

    const idsList = submissionIds.join(", ");

    try {
      await navigator.clipboard.writeText(idsList);
      enqueueSnackbar(
        `Copied ${submissionIds.length} ${
          submissionIds.length === 1 ? "ID" : "IDs"
        } to clipboard`,
        { variant: "success" }
      );
    } catch (e) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = idsList;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        enqueueSnackbar(
          `Copied ${submissionIds.length} ${
            submissionIds.length === 1 ? "ID" : "IDs"
          } to clipboard`,
          { variant: "success" }
        );
      } catch (err) {
        enqueueSnackbar("Failed to copy to clipboard", { variant: "error" });
      }
      document.body.removeChild(textArea);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    const ids = pastedText
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const { validCount, duplicateCount, invalidIds } = onAddIds(ids);

    if (invalidIds.length > 0) {
      const message = `Ignored ${invalidIds.length} invalid ${
        invalidIds.length === 1 ? "ID" : "IDs"
      }: ${invalidIds.join(", ")}`;
      enqueueSnackbar(message, { variant: "warning" });
    }

    if (duplicateCount > 0) {
      enqueueSnackbar(
        `Ignored ${duplicateCount} duplicate ${
          duplicateCount === 1 ? "ID" : "IDs"
        }`,
        { variant: "info" }
      );
    }

    if (validCount === 0 && invalidIds.length === 0 && duplicateCount === 0) {
      enqueueSnackbar("No IDs found in clipboard", { variant: "error" });
    }

    setSelectPasteTarget(false);
  };

  return (
    <>
      <Stack direction={"row"} spacing={1} alignItems={"center"}>
        <Button
          onClick={onReload}
          isDisabled={!hasUnsavedChanges}
          startIcon={<RefreshIcon />}
          aria-label={"Reload whitelist from server"}
          tooltip={reloadTooltip}
          tooltipRequired
        >
          {"Reload"}
        </Button>
        <Button
          onClick={onClear}
          isDisabled={submissionIds.length === 0}
          startIcon={<ClearIcon />}
          aria-label={"Clear all submissions"}
          tooltip={
            submissionIds.length === 0
              ? "No submissions to clear"
              : "Clear all whitelisted submissions"
          }
          tooltipRequired
        >
          {"Clear"}
        </Button>
        <Button
          onClick={handleCopy}
          isDisabled={submissionIds.length === 0}
          startIcon={<CopyIcon />}
          aria-label={"Copy submission IDs"}
          tooltip={
            submissionIds.length === 0
              ? "No IDs to copy"
              : "Copy all submission IDs to clipboard"
          }
          tooltipRequired
        >
          {"Copy IDs"}
        </Button>
        <Button
          onClick={() => setSelectPasteTarget(true)}
          startIcon={<PasteIcon />}
          aria-label={"Paste submission IDs"}
          tooltip={`Paste submission IDs from clipboard (${
            isMac ? "Cmd" : "Ctrl"
          }+V)`}
          tooltipRequired
        >
          {selectPasteTarget
            ? `Press ${isMac ? "Cmd" : "Ctrl"}-V now`
            : "Paste IDs"}
        </Button>
      </Stack>

      {/* Invisible paste input */}
      {selectPasteTarget && (
        <TextField
          inputRef={pasteInputRef}
          onPaste={handlePaste}
          onBlur={() => setSelectPasteTarget(false)}
          placeholder={""}
          size={"small"}
          sx={{
            position: "absolute",
            opacity: 0,
            pointerEvents: "none",
            width: 1,
            height: 1,
          }}
        />
      )}
    </>
  );
};

/**
 * WhitelistedSubmissions - List of whitelisted submission chips
 */
const WhitelistedSubmissions = ({
  submissionIds,
  allBySubmissionId,
  loadingWhitelist,
  loadingSubmissions,
  onRemove,
}) => {
  const theme = useTheme();
  if (loadingWhitelist || loadingSubmissions) {
    return (
      <>
        <CircularProgress size={20} />
        <Typography variant={"body2"} color={"text.secondary"}>
          {loadingWhitelist ? "Loading whitelist..." : "Loading submissions..."}
        </Typography>
      </>
    );
  }

  if (submissionIds.length === 0) {
    return (
      <Typography
        variant={"body2"}
        color={"text.secondary"}
        sx={{ fontStyle: "italic" }}
      >
        {"No submissions whitelisted for access"}
      </Typography>
    );
  }

  return (
    <Stack direction={"row"} spacing={1} flexWrap={"wrap"} useFlexGap gap={1}>
      {submissionIds.map((id) => {
        const submission = allBySubmissionId.get(id);
        const inception = submission?.inception
          ? dates.formatDate(submission.inception)
          : null;
        const expiration = submission?.expiration
          ? dates.formatDate(submission.expiration)
          : null;
        const dateRange =
          inception && expiration
            ? `${inception} - ${expiration}`
            : inception || expiration || null;
        const insuredName = submission?.insured || "Unknown";

        return (
          <Chip
            key={id}
            label={
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  pr: 5,
                  position: "relative",
                  userSelect: "text",
                  cursor: "text",
                }}
              >
                <Typography
                  variant={"body2"}
                  component={"div"}
                  sx={{
                    fontWeight: 500,
                    mb: 0.75,
                    userSelect: "text",
                    cursor: "text",
                  }}
                >
                  {insuredName}
                </Typography>
                <Typography
                  variant={"caption"}
                  component={"div"}
                  color={"text.secondary"}
                  sx={{
                    fontFamily: "monospace",
                    userSelect: "text",
                    cursor: "text",
                    mb: dateRange ? 0.5 : 0,
                  }}
                >
                  {id}
                </Typography>
                {dateRange && (
                  <Typography
                    variant={"caption"}
                    component={"div"}
                    color={"text.secondary"}
                    sx={{
                      fontSize: "0.75rem",
                      userSelect: "text",
                      cursor: "text",
                    }}
                  >
                    {dateRange}
                  </Typography>
                )}
              </Box>
            }
            onDelete={() => onRemove(id)}
            deleteIcon={
              <Tooltip title={"Remove access"}>
                <CloseIcon fontSize={"small"} />
              </Tooltip>
            }
            sx={{
              height: "auto",
              minWidth: "fit-content",
              backgroundColor: "transparent",
              border: "none",
              borderRadius: "10px",
              transition: "background-color 0.2s ease",
              "&:hover": {
                backgroundColor: `${theme.brand.accent[800]}26`,
              },
              "& .MuiChip-label": {
                display: "block",
                whiteSpace: "nowrap",
                position: "relative",
              },
              "& .MuiChip-deleteIcon": {
                position: "absolute",
                top: "12px",
                right: "8px",
                transition: "background-color 0.2s ease",
                borderRadius: "50%",
                padding: "4px",
                "&:hover": {
                  backgroundColor: `${theme.brand.black}33`,
                },
              },
            }}
          />
        );
      })}
    </Stack>
  );
};

/**
 * AllowAllAccessToggle - Switch to grant access to all submissions
 */
const AllowAllAccessToggle = ({
  allSubmissionsAllowed,
  loadingWhitelist,
  onToggle,
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleToggle = (event) => {
    const newValue = event.target.checked;
    if (newValue) {
      // Show confirmation dialog when enabling
      setShowConfirmDialog(true);
    } else {
      // Disable without confirmation
      onToggle(false);
    }
  };

  const handleConfirm = () => {
    onToggle(true);
    setShowConfirmDialog(false);
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      <Box
        onClick={() =>
          !loadingWhitelist &&
          handleToggle({ target: { checked: !allSubmissionsAllowed } })
        }
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          width: "fit-content",
          cursor: loadingWhitelist ? "default" : "pointer",
          userSelect: "none",
        }}
      >
        <Switch
          checked={allSubmissionsAllowed}
          onChange={handleToggle}
          disabled={loadingWhitelist}
          color={"primary"}
          aria-label={"Allow access to all submissions"}
        />
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant={"body2"}>
            {"Allow access to all submissions"}
          </Typography>
          <Typography variant={"caption"} color={"text.secondary"}>
            {allSubmissionsAllowed
              ? "User can access any submission in this company"
              : "User can only access whitelisted submissions"}
          </Typography>
        </Box>
      </Box>

      <Dialog open={showConfirmDialog} onClose={handleCancel}>
        <DialogTitle>{"Grant Access to All Submissions?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {
              "This will allow the user to access ALL submissions in this company, including any future submissions. Are you sure you want to proceed?"
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancel}
            variant={"text"}
            startIcon={<CloseIcon />}
            aria-label={"Cancel"}
          >
            {"Cancel"}
          </Button>
          <Button
            onClick={handleConfirm}
            variant={"contained"}
            color={"primary"}
            startIcon={<CheckCircleIcon />}
            aria-label={"Confirm grant access"}
          >
            {"Grant Access"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/**
 * SubmissionAccessEditor
 * - Props: userId (required)
 * - Behavior: loads whitelist, shows blocks for valid/invalid IDs, supports paste and dropdown selection
 */
const SubmissionAccessEditor = ({
  userId,
  whitelist,
  loadingWhitelist,
  loadError,
  whitelistList,
  loadingWhitelistList,
  loadErrorList,
  onReload,
}) => {
  const { companyId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: allSubmissions,
    isFetching: loadingSubmissions,
  } = useListSubmissionsQuery({
    impersonateCompanyId: companyId,
  });

  const [
    updateWhitelistList,
    { isLoading: savingList },
  ] = useUpdateUserWhitelistListMutation();

  const [
    updateWhitelist,
    { isLoading: savingAccessList },
  ] = useUpdateUserWhitelistMutation();

  const saving = savingAccessList || savingList;
  const [submissionIds, setSubmissionIds] = useState([]);
  const [allSubmissionsAllowed, setAllSubmissionsAllowed] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [isListingWhiteList, setIsListingWhiteList] = useState(false);

  // Initialize from whitelist
  useEffect(() => {
    if (isListingWhiteList) {
      if (loadErrorList) {
        setSubmissionIds([]);
        setAllSubmissionsAllowed(false);
        return;
      }
      if (whitelistList) {
        const ids = whitelistList?.submissionIdWhitelist ?? [];
        setSubmissionIds(ids);
        setAllSubmissionsAllowed(whitelistList?.allSubmissionsAllowed ?? false);
      }
    } else {
      if (loadError) {
        setSubmissionIds([]);
        setAllSubmissionsAllowed(false);
        return;
      }
      if (whitelist) {
        const ids = whitelist?.submissionIdWhitelist ?? [];
        setSubmissionIds(ids);
        setAllSubmissionsAllowed(whitelist?.allSubmissionsAllowed ?? false);
      }
    }
  }, [
    whitelist,
    loadError,
    loadingWhitelist,
    isListingWhiteList,
    loadErrorList,
    whitelistList,
  ]);

  // Check if current state matches loaded state (order-insensitive)
  const hasUnsavedChanges = useMemo(() => {
    const loadedIds =
      (isListingWhiteList ? whitelistList : whitelist)?.submissionIdWhitelist ??
      [];
    const loadedAll = Boolean(
      (isListingWhiteList ? whitelistList : whitelist)?.allSubmissionsAllowed
    );
    if (allSubmissionsAllowed !== loadedAll) return true;
    if (loadedIds.length !== submissionIds.length) return true;
    const counts = new Map();
    for (const id of loadedIds) counts.set(id, (counts.get(id) ?? 0) + 1);
    for (const id of submissionIds) {
      const next = (counts.get(id) ?? 0) - 1;
      if (next < 0) return true;
      counts.set(id, next);
    }
    // All counts must be zero
    for (const v of counts.values()) if (v !== 0) return true;
    return false;
  }, [
    isListingWhiteList,
    whitelistList,
    whitelist,
    allSubmissionsAllowed,
    submissionIds,
  ]);

  const allBySubmissionId = useMemo(() => {
    const map = new Map();
    for (const s of allSubmissions ?? []) {
      if (s?.submissionId) map.set(String(s.submissionId), s);
    }
    return map;
  }, [allSubmissions]);

  // Add IDs with duplicate detection
  const addIds = useCallback(
    (newIds) => {
      const filtered = newIds.filter((id) => id && id.trim().length > 0);
      let duplicateCount = 0;
      const toAdd = [];
      const invalidIds = [];
      const seenInBatch = new Set();

      for (const id of filtered) {
        const trimmedId = id.trim();

        // Check if already in existing whitelist
        if (submissionIds.includes(trimmedId)) {
          duplicateCount++;
        }
        // Check if duplicate within this batch
        else if (seenInBatch.has(trimmedId)) {
          duplicateCount++;
        }
        // Valid and new
        else if (allBySubmissionId.has(trimmedId)) {
          toAdd.push(trimmedId);
          seenInBatch.add(trimmedId);
        }
        // Invalid
        else {
          invalidIds.push(trimmedId);
        }
      }

      if (toAdd.length > 0) {
        setSubmissionIds((prev) => [...prev, ...toAdd]);
      }

      return { validCount: toAdd.length, duplicateCount, invalidIds };
    },
    [submissionIds, allBySubmissionId]
  );

  const removeSubmissionId = useCallback((id) => {
    setSubmissionIds((prev) => prev.filter((sid) => sid !== id));
  }, []);

  // Handle submission change from selector
  useEffect(() => {
    if (!selectedSubmission) return;
    const id = String(selectedSubmission?.submissionId ?? "");
    if (!id) return;
    const { duplicateCount } = addIds([id]);

    if (duplicateCount > 0) {
      enqueueSnackbar("This submission is already in the whitelist", {
        variant: "info",
      });
    }

    setSelectedSubmission(null);
  }, [selectedSubmission, addIds, enqueueSnackbar]);

  const canSave = userId && !loadingSubmissions && hasUnsavedChanges;

  const onSave = async () => {
    if (!canSave) return;
    try {
      const updList = async () => {
        const fn = isListingWhiteList ? updateWhitelistList : updateWhitelist;
        return fn({
          userId,
          submissionIds,
          allSubmissionsAllowed,
        }).unwrap();
      };
      const result = await updList();
      if (result?.error) {
        enqueueSnackbar("Failed to save whitelist", { variant: "error" });
        return;
      }
      enqueueSnackbar("Whitelist saved", { variant: "success" });
      onReload();
    } catch (e) {
      enqueueSnackbar("Failed to save whitelist", { variant: "error" });
    }
  };
  const ACCESS_WHITELIST = "IS_ACCESS_WHITE_LIST";
  const LIST_WHITELIST = "IS_LIST_WHITE_LIST";
  return (
    <Stack spacing={2}>
      {/* Header */}
      <Grid
        sx={{ width: "500px" }}
        container
        spacing={2}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Grid item>
          <Typography variant={"h6"}>{"User Whitelist"}</Typography>
        </Grid>
        <Grid item>
          <Select
            onChange={() => setIsListingWhiteList(!isListingWhiteList)}
            value={isListingWhiteList ? LIST_WHITELIST : ACCESS_WHITELIST}
          >
            <MenuItem value={ACCESS_WHITELIST}>
              {"Submissions Access Whitelist"}
            </MenuItem>
            <MenuItem value={LIST_WHITELIST}>
              {"Submissions Listing Whitelist"}
            </MenuItem>
          </Select>
        </Grid>
      </Grid>
      <WhitelistManagement
        hasUnsavedChanges={hasUnsavedChanges}
        submissionIds={submissionIds}
        onClear={() => {
          setSubmissionIds([]);
          setAllSubmissionsAllowed(false);
        }}
        onReload={onReload}
        onAddIds={addIds}
      />

      {hasUnsavedChanges && (
        <Chip
          label={"Unsaved changes"}
          size={"small"}
          color={"warning"}
          variant={"outlined"}
          sx={{ width: "fit-content", userSelect: "none", cursor: "default" }}
        />
      )}

      <Box sx={{ padding: 3 }}>
        <WhitelistedSubmissions
          submissionIds={submissionIds}
          allBySubmissionId={allBySubmissionId}
          loadingWhitelist={
            isListingWhiteList ? loadingWhitelistList : loadingWhitelist
          }
          loadingSubmissions={loadingSubmissions}
          onRemove={removeSubmissionId}
        />
      </Box>

      <SubmissionSelector
        companyId={companyId}
        value={selectedSubmission}
        onChange={setSelectedSubmission}
        label={"Select Submission to grant user access"}
        filterSubmissions={(s) =>
          !submissionIds.includes(String(s?.submissionId ?? ""))
        }
        size={"small"}
        fullWidth={false}
        sx={{ width: "30em" }}
      />

      <AllowAllAccessToggle
        allSubmissionsAllowed={allSubmissionsAllowed}
        loadingWhitelist={
          isListingWhiteList ? loadingWhitelistList : loadingWhitelist
        }
        onToggle={setAllSubmissionsAllowed}
      />

      <Stack direction={"row"} spacing={1} alignItems={"center"}>
        <Button
          onClick={onSave}
          isDisabled={!canSave || saving}
          isLoading={saving}
          startIcon={<SaveIcon />}
          aria-label={"Save whitelist changes"}
          tooltip={
            hasUnsavedChanges
              ? "Save changes to whitelist"
              : "No changes to save"
          }
          tooltipRequired
        >
          {"Save"}
        </Button>
      </Stack>
    </Stack>
  );
};

export default SubmissionAccessEditor;
